import React from 'react';

/**
 * Lightweight Markdown renderer — no external dependencies.
 *
 * Supported syntax:
 *   Block:   # h1  ## h2  ### h3  #### h4
 *            > blockquote
 *            ```lang ... ```  (fenced code blocks)
 *            - / * / + unordered list
 *            1. ordered list
 *            | table | syntax |
 *            --- / *** / ___ horizontal rule
 *            blank line → paragraph break
 *   Inline:  **bold**  *italic*  ~~strike~~  `code`
 *            [text](url)  ![alt](url)
 */

// ─── Inline renderer ─────────────────────────────────────────────────────────

function renderInline(text: string, key?: number): React.ReactNode {
  const TOKEN = /(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__|_[^_]+_|~~[^~]+~~|`[^`]+`|!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\))/g;
  const parts = text.split(TOKEN);
  return (
    <React.Fragment key={key}>
      {parts.map((part, i) => {
        // Bold + italic
        if (/^\*\*\*[^*]+\*\*\*$/.test(part))
          return <strong key={i}><em>{part.slice(3, -3)}</em></strong>;
        // Bold
        if (/^\*\*[^*]+\*\*$/.test(part) || /^__[^_]+__$/.test(part))
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        // Italic
        if (/^\*[^*]+\*$/.test(part) || /^_[^_]+_$/.test(part))
          return <em key={i}>{part.slice(1, -1)}</em>;
        // Strikethrough
        if (/^~~[^~]+~~$/.test(part))
          return <del key={i}>{part.slice(2, -2)}</del>;
        // Inline code
        if (/^`[^`]+`$/.test(part))
          return <code key={i} className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-rose-600">{part.slice(1, -1)}</code>;
        // Image ![alt](url)
        const img = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
        if (img)
          return <img key={i} src={img[2]} alt={img[1]} className="inline max-w-full rounded" />;
        // Link [text](url)
        const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link)
          return <a key={i} href={link[2]} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">{link[1]}</a>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </React.Fragment>
  );
}

// ─── Block tokeniser ─────────────────────────────────────────────────────────

type MdToken =
  | { t: 'h'; level: 1 | 2 | 3 | 4; text: string }
  | { t: 'hr' }
  | { t: 'code'; lang: string; lines: string[] }
  | { t: 'blockquote'; lines: string[] }
  | { t: 'ul'; items: string[] }
  | { t: 'ol'; items: string[] }
  | { t: 'table'; headers: string[]; rows: string[][] }
  | { t: 'p'; lines: string[] };

function tokenise(md: string): MdToken[] {
  const lines = md.split('\n');
  const tokens: MdToken[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line — skip
    if (line.trim() === '') { i++; continue; }

    // Fenced code block
    const fenceMatch = line.match(/^(`{3,}|~{3,})([\w-]*)/);
    if (fenceMatch) {
      const fence = fenceMatch[1];
      const lang = fenceMatch[2] || '';
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith(fence)) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // closing fence
      tokens.push({ t: 'code', lang, lines: codeLines });
      continue;
    }

    // Heading
    const hMatch = line.match(/^(#{1,4})\s+(.+)/);
    if (hMatch) {
      tokens.push({ t: 'h', level: Math.min(hMatch[1].length, 4) as 1|2|3|4, text: hMatch[2] });
      i++; continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}\s*$/.test(line.trim())) {
      tokens.push({ t: 'hr' });
      i++; continue;
    }

    // Blockquote
    if (line.startsWith('> ') || line === '>') {
      const bqLines: string[] = [];
      while (i < lines.length && (lines[i].startsWith('> ') || lines[i] === '>')) {
        bqLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      tokens.push({ t: 'blockquote', lines: bqLines });
      continue;
    }

    // Unordered list
    if (/^[-*+] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+] /.test(lines[i])) {
        items.push(lines[i].replace(/^[-*+] /, ''));
        i++;
      }
      tokens.push({ t: 'ul', items });
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      tokens.push({ t: 'ol', items });
      continue;
    }

    // Table (must have | and a separator row next)
    if (line.includes('|') && i + 1 < lines.length && /^\|?[-:| ]+\|?$/.test(lines[i + 1])) {
      const parseRow = (r: string) =>
        r.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const headers = parseRow(line);
      i += 2; // skip separator
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|')) {
        rows.push(parseRow(lines[i]));
        i++;
      }
      tokens.push({ t: 'table', headers, rows });
      continue;
    }

    // Paragraph — collect consecutive non-special lines
    const pLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^#{1,4} /.test(lines[i]) &&
      !/^[-*+] /.test(lines[i]) &&
      !/^\d+\. /.test(lines[i]) &&
      !/^(`{3,}|~{3,})/.test(lines[i]) &&
      !/^> /.test(lines[i]) &&
      !/^[-*_]{3,}\s*$/.test(lines[i].trim()) &&
      !lines[i].includes('|')
    ) {
      pLines.push(lines[i]);
      i++;
    }
    if (pLines.length) tokens.push({ t: 'p', lines: pLines });
  }

  return tokens;
}

// ─── Renderer ────────────────────────────────────────────────────────────────

export function renderMarkdown(md: string): React.ReactNode {
  if (!md?.trim()) return null;
  const tokens = tokenise(md);

  return (
    <div className="md-content">
      {tokens.map((tok, idx) => {
        switch (tok.t) {
          case 'h': {
            const sizes = ['text-3xl', 'text-2xl', 'text-xl', 'text-lg'];
            const Tag = `h${tok.level}` as 'h1' | 'h2' | 'h3' | 'h4';
            return (
              <Tag
                key={idx}
                className={`${sizes[tok.level - 1]} font-bold text-gray-900 mt-5 mb-2 leading-tight`}
              >
                {renderInline(tok.text)}
              </Tag>
            );
          }

          case 'hr':
            return <hr key={idx} className="border-t-2 border-gray-200 my-4" />;

          case 'code':
            return (
              <div key={idx} className="my-3 rounded-lg overflow-hidden">
                {tok.lang && (
                  <div className="bg-gray-800 px-4 py-1 text-xs text-gray-400 font-mono">{tok.lang}</div>
                )}
                <pre className="bg-gray-900 text-green-300 px-4 py-3 overflow-x-auto text-sm font-mono leading-relaxed">
                  <code>{tok.lines.join('\n')}</code>
                </pre>
              </div>
            );

          case 'blockquote':
            return (
              <blockquote key={idx} className="border-l-4 border-gray-300 pl-4 my-3 text-gray-600 italic">
                {tok.lines.map((l, i) => (
                  <p key={i} className="mb-1 last:mb-0">{renderInline(l)}</p>
                ))}
              </blockquote>
            );

          case 'ul':
            return (
              <ul key={idx} className="list-disc list-inside my-3 space-y-1 text-gray-700 pl-4">
                {tok.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ul>
            );

          case 'ol':
            return (
              <ol key={idx} className="list-decimal list-inside my-3 space-y-1 text-gray-700 pl-4">
                {tok.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ol>
            );

          case 'table':
            return (
              <div key={idx} className="my-4 overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {tok.headers.map((h, i) => (
                        <th key={i} className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">
                          {renderInline(h)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tok.rows.map((row, ri) => (
                      <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-4 py-2 border-b border-gray-100 text-gray-700">
                            {renderInline(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case 'p':
            return (
              <p key={idx} className="text-gray-700 leading-relaxed my-2">
                {tok.lines.map((line, i) => (
                  <React.Fragment key={i}>
                    {renderInline(line)}
                    {i < tok.lines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}
